// PanelFocusHeader — the right panel's ONE header row, pure.
//
// Home: the panel's name ("Context") + close. Focused: a back arrow, then the
// breadcrumb "Context > {title}" (the kind lives in the icon, not in words;
// "Context > {course} > {part}" for a course opened at a part), then
// "open in library" and close. "Context" in the breadcrumb returns home; the
// back arrow returns ONE step (to the claim's material when the entity was
// reached from one, else home). Long titles truncate with an ellipsis + tooltip.
//
// UI-as-projection: props-driven, owns no state. Every intent is reported up
// (onFocusOverview / onBack / onOpenHome / onClose). `HeaderFocus` is a
// component-local projection of the frame's `PanelFocus`, so this primitive
// imports nothing from `app/`.

import { useLingui } from "@lingui/react/macro";
import { Icon, type IconName } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import type { ResourceKind, ResourceRef } from "../Navigation/types.js";
import "./PanelFocusHeader.css";

// Where the back arrow returns to: home, or the material a source was reached
// from (a claim's citation).
type HeaderBack =
    | { readonly type: "home" }
    | { readonly type: "material"; readonly title: string };

// `part` = the part of `ref` opened (a course's module): the breadcrumb then
// reads "Context > course > part", and the course crumb opens the whole course.
export type HeaderFocus =
    | { readonly type: "overview" }
    | {
          readonly type: "entity";
          readonly ref: ResourceRef;
          readonly back: HeaderBack;
          readonly part?: ResourceRef;
      };

export type PanelFocusHeaderProps = {
    readonly focus: HeaderFocus;
    readonly onFocusOverview: () => void;
    readonly onBack: () => void;
    // Absent when a surrounding frame owns close (the chat panel's tab bar):
    // the overview then needs no header row at all (its tab names it).
    readonly onClose?: () => void;
    // "Open in library" (a material) or "Open in the knowledge bank" (a
    // source) on a focused entity: the author's explicit choice to leave the
    // chat for the entity's home. Absent => not offered.
    readonly onOpenHome?: (ref: ResourceRef) => void;
    // The course crumb of a focused part: open the whole course.
    readonly onFocusWhole?: (ref: ResourceRef) => void;
};

const iconForKind: Record<ResourceKind, IconName> = {
    source: "source",
    material: "material",
    export: "export",
    file: "file",
};

export const PanelFocusHeader = ({
    focus,
    onFocusOverview,
    onBack,
    onClose,
    onOpenHome,
    onFocusWhole,
}: PanelFocusHeaderProps) => {
    const { t } = useLingui();
    const closeButton =
        onClose === undefined ? null : (
            <IconButton
                label={t`Close context`}
                variant="ghost"
                size="sm"
                onClick={onClose}
            >
                <Icon name="close" />
            </IconButton>
        );

    if (focus.type === "overview") {
        if (closeButton === null) return null;
        return (
            <div className="bilda-panel-focus-header">
                <h2 className="bilda-panel-focus-header__title">{t`Context`}</h2>
                <div className="bilda-panel-focus-header__actions">
                    {closeButton}
                </div>
            </div>
        );
    }

    const { back } = focus;
    // The crumb that is current: the part when one is open, else the entity.
    const ref = focus.part ?? focus.ref;
    const sep = (
        <span className="bilda-panel-focus-header__sep" aria-hidden="true">
            <Icon name="chevron-right" size="xs" />
        </span>
    );
    const backLabel =
        back.type === "home" ? t`Back to context` : t`Back to ${back.title}`;
    return (
        <div className="bilda-panel-focus-header">
            <IconButton
                label={backLabel}
                variant="ghost"
                size="sm"
                onClick={onBack}
            >
                <Icon name="chevron-left" />
            </IconButton>
            <nav
                className="bilda-panel-focus-header__crumbs"
                aria-label={t`Context`}
            >
                <button
                    type="button"
                    className="bilda-panel-focus-header__crumb"
                    onClick={onFocusOverview}
                >
                    {t`Context`}
                </button>
                {sep}
                {focus.part === undefined ? null : (
                    <>
                        <button
                            type="button"
                            className="bilda-panel-focus-header__crumb"
                            title={focus.ref.title}
                            onClick={() => onFocusWhole?.(focus.ref)}
                        >
                            {focus.ref.title}
                        </button>
                        {sep}
                    </>
                )}
                <span
                    className="bilda-panel-focus-header__current"
                    aria-current="page"
                    title={ref.title}
                >
                    <Icon name={iconForKind[ref.type]} size="xs" />
                    <span className="bilda-panel-focus-header__current-title">
                        {ref.title}
                    </span>
                </span>
            </nav>
            <div className="bilda-panel-focus-header__actions">
                {onOpenHome !== undefined ? (
                    <IconButton
                        label={
                            ref.type === "source" || ref.type === "file"
                                ? t`Open in the knowledge bank`
                                : t`Open in library`
                        }
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenHome(ref)}
                    >
                        <Icon name="external-link" />
                    </IconButton>
                ) : null}
                {closeButton}
            </div>
        </div>
    );
};
